<script lang="ts">
import { computed, defineComponent, unref } from 'vue';

import { classesStates } from '@components/utils';

import { makeItemsProps, makeThemeProps, provideTheme, useItems } from '../../composable';

import type { IInternalItem } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsStages';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Текущий этап
     */
    current: {
      type: [String, Number] as PropType<string | number | null>,
      default: null
    },
    /**
     * Завершены все этапы
     */
    finished: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeItemsProps(),
    ...makeThemeProps()
  },
  setup(props, { expose }) {
    const { items } = useItems(props);
    const { themeClasses } = provideTheme(props);

    const currentStageIndex = computed(() => {
      return unref(items).findIndex((item) => String(item.value) === String(props.current));
    });

    const innerStyle = computed(() => {
      return `grid-template-columns: repeat(${unref(items).length}, 1fr);`;
    });

    const isFinished = computed(() => {
      return props.finished || String(props.current) === '0';
    });

    const getStageClasses = (item: IInternalItem, idx: number) => ({
      [classesStates.active]: !unref(isFinished) && String(item.value) === String(props.current),
      [classesStates.invalid]: item.props?.error && idx <= currentStageIndex.value,
      [classesStates.checked]: unref(isFinished) || idx < currentStageIndex.value
    });

    const isCurrent = (idx: number) => {
      return idx === unref(currentStageIndex);
    };

    const hasError = (item: IInternalItem) => {
      return item.props?.error;
    };

    expose({
      isCurrent,
      isFinished
    });

    return {
      getStageClasses,
      isCurrent,
      isFinished,
      hasError,
      innerStyle,
      themeClasses
    };
  }
});
</script>

<template>
  <ul class="cds-stages" :class="themeClasses" :style="innerStyle">
    <li v-for="(item, idx) in items" :key="item.value" class="cds-stage" :class="getStageClasses(item, idx)">
      <span class="cds-stage__text">{{ item.title }}</span>
      <svg
        v-if="idx !== items.length - 1"
        width="16"
        height="40"
        viewBox="0 0 16 40"
        xmlns="http://www.w3.org/2000/svg"
        class="cds-stage__arrow"
      >
        <path d="M0 0L4.19048 7.27177e-06L13 20L4.19048 40L0 40V0Z" />
        <path d="M0 39H4.51263L14 20L4.51263 1H0" />
      </svg>
    </li>
  </ul>
</template>

<style lang="scss">
@import './CdsStages';
</style>
